import React from 'react';
import { ShoppingCart, BarChart3, Database, FileSpreadsheet, Lock } from 'lucide-react';

export type IntegrationProvider = 'woocommerce' | 'shopify' | 'zoho' | 'powerbi';

interface IntegrationOptionProps {
  provider: IntegrationProvider;
  isActive: boolean;
  isSelected: boolean;
  onSelect: () => void;
}

const providerConfig: Record<IntegrationProvider, {
  name: string;
  description: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
}> = {
  woocommerce: {
    name: 'WooCommerce',
    description: 'Sync your WooCommerce store catalog',
    icon: ShoppingCart,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
  },
  shopify: {
    name: 'Shopify',
    description: 'Connect your Shopify store',
    icon: ShoppingCart,
    color: 'text-green-600',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
  },
  zoho: {
    name: 'Zoho Analytics',
    description: 'Business intelligence and analytics',
    icon: BarChart3,
    color: 'text-orange-600',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
  },
  powerbi: {
    name: 'Power BI',
    description: 'Microsoft Power BI integration',
    icon: FileSpreadsheet,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
  },
};

const IntegrationOption: React.FC<IntegrationOptionProps> = ({
  provider,
  isActive,
  isSelected,
  onSelect,
}) => {
  const config = providerConfig[provider];
  const Icon = config.icon;

  return (
    <button
      type="button"
      onClick={isActive ? onSelect : undefined}
      disabled={!isActive}
      className={`relative w-full p-4 rounded-lg border-2 text-left transition-all duration-200
        ${isActive
          ? isSelected
            ? 'border-primary bg-primary/5 dark:bg-primary/10'
            : 'border-gray-200 dark:border-gray-700 hover:border-primary/50 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer'
          : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/30 opacity-60 cursor-not-allowed'
        }`}
    >
      {/* Coming Soon badge */}
      {!isActive && (
        <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full bg-gray-200 dark:bg-gray-700 text-xs font-medium text-gray-600 dark:text-gray-400">
          <Lock className="w-3 h-3" />
          Coming Soon
        </div>
      )}

      <div className="flex items-start gap-4">
        <div className={`p-3 rounded-lg ${config.bgColor}`}>
          <Icon className={`w-6 h-6 ${config.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={`font-semibold ${isActive ? 'dark:text-white text-gray-900' : 'text-gray-500 dark:text-gray-500'}`}>
            {config.name}
          </h3>
          <p className={`text-sm mt-1 ${isActive ? 'text-gray-600 dark:text-gray-400' : 'text-gray-400 dark:text-gray-600'}`}>
            {config.description}
          </p>
        </div>
        
        {/* Selection indicator */}
        {isActive && (
          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1
            ${isSelected ? 'border-primary bg-primary' : 'border-gray-300 dark:border-gray-600'}`}
          >
            {isSelected && (
              <div className="w-2 h-2 rounded-full bg-white" />
            )}
          </div>
        )}
      </div>
    </button>
  );
};

export default IntegrationOption;
export { providerConfig };
